/* Wunschlieferwochen: date-only ISO arithmetic, shared PDP/cart state. */
(function (root) {
  'use strict';
  const DAY = 86400000;
  const WEEK = 7 * DAY;
  const GROUP = '_cstm_delivery_group';
  const CODE = '_cstm_wunschlieferwoche';
  const LABEL = 'Wunschlieferwoche';
  function date(value) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return null;
    const result = Date.parse(value + 'T00:00:00Z');
    return Number.isFinite(result) && new Date(result).toISOString().slice(0, 10) === value ? result : null;
  }
  function monday(value) { return value - ((new Date(value).getUTCDay() + 6) % 7) * DAY; }
  function berlinToday(now = new Date()) {
    const parts = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Berlin', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
    const get = type => parts.find(p => p.type === type).value;
    return date(`${get('year')}-${get('month')}-${get('day')}`);
  }
  function codeFor(start) {
    const year = new Date(start + 3 * DAY).getUTCFullYear();
    const first = monday(date(`${year}-01-04`));
    return `${year}-W${String(1 + Math.round((start - first) / WEEK)).padStart(2, '0')}`;
  }
  function label(code) { if (!/^\d{4}-W\d{2}$/.test(code || '')) return code || ''; return `KW ${Number(code.slice(6))} / ${code.slice(0, 4)}`; }
  function weeks(from, to, now = new Date()) {
    const a = date(from), b = date(to);
    if (a === null || b === null || a > b) return [];
    const result = [];
    const fmt = new Intl.DateTimeFormat('de-DE', { timeZone: 'UTC', day: '2-digit', month: '2-digit', year: 'numeric' });
    const pad = value => String(value).padStart(2, '0');
    for (let start = Math.max(monday(a), monday(berlinToday(now)) + WEEK); start <= monday(b); start += WEEK) {
      const code = codeFor(start);
      const end = start + 6 * DAY;
      const first = new Date(start), last = new Date(end);
      let fromLabel = fmt.format(start);
      if (first.getUTCFullYear() === last.getUTCFullYear()) {
        fromLabel = `${pad(first.getUTCDate())}.${pad(first.getUTCMonth() + 1)}.`;
      }
      result.push({ code, label: `KW ${code.slice(6)} | ${fromLabel} – ${fmt.format(end)}` });
    }
    return result;
  }
  function properties(existing, code, group) {
    const result = { ...existing, [GROUP]: group };
    delete result[CODE]; delete result[LABEL];
    if (code && result._is_montage_service !== 'true') {
      result[CODE] = code; result[LABEL] = label(code);
    }
    return result;
  }
  function belongs(child, parent) {
    if (!child.properties?._karibu_parent) return false;
    if (child.properties[GROUP] || parent.properties?.[GROUP]) {
      return Boolean(child.properties[GROUP]) && child.properties[GROUP] === parent.properties?.[GROUP];
    }
    return String(child.properties._karibu_parent) === String(parent.variant_id);
  }
  function members(cart, parent) {
    if (parent.properties?.[GROUP]) return cart.items.filter(i => i.key === parent.key || belongs(i, parent));
    const children = cart.items.filter(i => !i.properties?.[GROUP] && belongs(i, parent));
    const parents = cart.items.filter(i => !i.properties?._karibu_parent && String(i.variant_id) === String(parent.variant_id));
    if (children.length && parents.length !== 1) throw new Error('Zubehör ist nicht eindeutig zugeordnet. Bitte diese Konfiguration entfernen und erneut hinzufügen.');
    return [parent, ...children];
  }
  function identity(item) {
    const props = { ...item.properties };
    delete props[GROUP]; delete props[CODE]; delete props[LABEL];
    return JSON.stringify([item.variant_id, Object.entries(props).sort(([a], [b]) => a.localeCompare(b)), item.selling_plan_allocation?.selling_plan?.id || null]);
  }
  const api = { weeks, codeFor, date, berlinToday, properties, belongs, members, identity };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (!root.document || root.CstmDeliveryWeek) return;
  root.CstmDeliveryWeek = api;
  const doc = root.document;
  // Native selects may match :focus-visible after pointer selection as well.
  doc.addEventListener('keydown', (event) => {
    if (!event.altKey && !event.ctrlKey && !event.metaKey && !['Shift', 'Control', 'Alt', 'Meta'].includes(event.key)) {
      doc.documentElement.setAttribute('data-cstm-delivery-keyboard', '');
    }
  }, true);
  doc.addEventListener('pointerdown', () => {
    doc.documentElement.removeAttribute('data-cstm-delivery-keyboard');
  }, true);
  const selector = 'cstm-delivery-week';
  const uuid = () => root.crypto.randomUUID();
  const endpoint = path => (root.Shopify?.routes?.root || '/') + path;
  const journalKey = 'cstm-delivery-week-pending';
  let busy = false, unresolved = false, latestCart = null;
  let memoryJournal = null;
  let noticeTimer;
  const disabledControls = new Map();
  const productSelections = new Map();
  function readJournal() {
    try { return JSON.parse(sessionStorage.getItem(journalKey)) || memoryJournal; } catch (_) { return memoryJournal; }
  }
  function saveJournal(value) {
    memoryJournal = value;
    try { if (value) sessionStorage.setItem(journalKey, JSON.stringify(value)); else sessionStorage.removeItem(journalKey); } catch (_) { /* memory fallback */ }
  }
  async function request(path, body) {
    const response = await fetch(endpoint(path), { method: body ? 'POST' : 'GET', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, ...(body ? { body: JSON.stringify(body) } : {}), cache: 'no-store' });
    const data = await response.json();
    if (!response.ok || data.errors) throw new Error(data.description || data.message || 'Der Warenkorb konnte nicht aktualisiert werden. Bitte erneut versuchen.');
    return data;
  }
  // During customElements.define(), existing elements upgrade one at a time.
  // A sibling cart picker may still be a plain HTMLElement at this point.
  const controls = () => [...doc.querySelectorAll(selector)].filter(el => el.input && typeof el.update === 'function');
  const cartControls = () => controls().filter(el => el.dataset.mode === 'cart');
  function notice(message) {
    clearTimeout(noticeTimer);
    let box = doc.getElementById('cstm-delivery-status');
    if (!box) {
      box = doc.createElement('div'); box.id = 'cstm-delivery-status'; box.setAttribute('role', 'status');
      doc.body.append(box);
    }
    box.replaceChildren(); box.hidden = !message;
    if (message) box.append(doc.createTextNode(message));
    if (readJournal()) {
      const retry = doc.createElement('button'); retry.type = 'button'; retry.textContent = 'Erneut versuchen';
      retry.addEventListener('click', () => mutate(null)); box.append(retry);
    } else if (message === 'Wunschlieferwoche aktualisiert.') {
      noticeTimer = setTimeout(() => { box.hidden = true; }, 4000);
    }
  }
  function lock() {
    const invalid = cartControls().some(el => el.invalid);
    if (busy) {
      doc.querySelectorAll('mini-cart select, mini-cart input, mini-cart button, cart-items select, cart-items input, cart-items button, [name="checkout"]').forEach(el => {
        if (!disabledControls.has(el)) disabledControls.set(el, el.disabled);
        el.disabled = true;
      });
    } else {
      disabledControls.forEach((disabled, el) => { el.disabled = disabled; });
      disabledControls.clear();
    }
    doc.documentElement.classList.toggle('cstm-delivery-busy', busy);
    doc.documentElement.classList.toggle('cstm-delivery-blocked', busy || unresolved || invalid || Boolean(readJournal()));
    doc.documentElement.classList.toggle('cstm-delivery-cart', cartControls().some(el => el.available.length || el.dataset.current));
  }
  async function refresh() {
    const surfaces = [...doc.querySelectorAll('mini-cart, cart-items')];
    await Promise.all(surfaces.map(el => el.updateSectionContents()));
    if (latestCart) {
      surfaces.forEach(el => el.classList.toggle('is-empty', latestCart.item_count === 0));
      doc.getElementById('main-cart-footer')?.classList.toggle('is-empty', latestCart.item_count === 0);
      if (!latestCart.item_count) cartControls().forEach(el => el.remove());
    }
    lock();
    doc.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
  }
  function publishCartUpdate(source, variantId) {
    if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source, cartData: latestCart, variantId });
    }
  }
  function checkGroups(cart, onlyGroup) {
    for (const parent of cart.items.filter(i => !i.properties?._karibu_parent && i.properties?.[GROUP] && (!onlyGroup || i.properties[GROUP] === onlyGroup))) {
      for (const item of members(cart, parent)) {
        if (item.properties?._is_montage_service === 'true') continue;
        if ((item.properties?.[CODE] || '') !== (parent.properties?.[CODE] || '') || (item.properties?.[LABEL] || '') !== (parent.properties?.[LABEL] || '')) return false;
      }
    }
    return true;
  }
  async function execute(journal) {
    let cart = await request('cart.js');
    if (cart.token !== journal.token) { saveJournal(null); throw new Error('Der Warenkorb wurde gewechselt. Bitte Auswahl erneut treffen.'); }
    for (const target of journal.targets) {
      // Cart keys change with properties. Resolve again after every mutation.
      let item = cart.items.find(i => i.key === target.key);
      if (!item) {
        const matches = cart.items.filter(i => i.properties?.[GROUP] === journal.group && identity(i) === target.identity);
        if (matches.length === 1) item = matches[0];
      }
      if (!item) throw new Error('Die Konfiguration wurde inzwischen geändert. Bitte den Warenkorb neu laden und die betroffene Konfiguration erneut hinzufügen.');
      const props = properties(item.properties, journal.code, journal.group);
      cart = await request('cart/change.js', { id: item.key, quantity: item.quantity, properties: props });
      const updated = cart.items.find(i => i.properties?.[GROUP] === journal.group && identity(i) === target.identity);
      if (!updated || updated.quantity !== item.quantity || JSON.stringify(Object.entries(updated.properties).sort()) !== JSON.stringify(Object.entries(props).sort())) throw new Error('Die Wunschlieferwoche konnte nicht vollständig gespeichert werden. Bitte erneut versuchen.');
    }
    latestCart = await request('cart.js');
    if (!checkGroups(latestCart, journal.group)) throw new Error('Die Lieferwochen der Konfiguration stimmen noch nicht überein. Bitte erneut versuchen.');
    saveJournal(null);
  }
  async function mutate(control) {
    if (busy) return;
    const requestedCode = control?.input.value || '';
    if (control && requestedCode && !control.available.some(w => w.code === requestedCode)) return;
    busy = true; lock(); notice('Wunschlieferwoche wird gespeichert …');
    try {
      const mini = doc.querySelector('mini-cart');
      await mini?.flushPendingQuantityUpdates();
      await mini?.mutationQueue;
      let journal = readJournal();
      if (journal && control) {
        if (journal.targets.some(t => t.key === control.dataset.key) || control.dataset.group === journal.group) {
          journal.code = requestedCode;
          saveJournal(journal);
        } else {
          throw new Error('Bitte zuerst die ausstehende Änderung der anderen Konfiguration abschließen.');
        }
      }
      if (!journal && control) {
        const cart = await request('cart.js');
        const parent = cart.items.find(i => i.key === control.dataset.key);
        if (!parent) throw new Error('Der Artikel wurde inzwischen geändert. Bitte Auswahl erneut treffen.');
        const group = parent.properties?.[GROUP] || uuid();
        journal = { token: cart.token, group, code: requestedCode, targets: members(cart, parent).map(i => ({ key: i.key, identity: identity(i) })) };
        saveJournal(journal);
      }
      if (journal) await execute(journal);
      unresolved = latestCart ? !checkGroups(latestCart) : false;
      await refresh();
      if (journal) {
        const parent = latestCart.items.find(item => !item.properties?._karibu_parent && item.properties?.[GROUP] === journal.group);
        publishCartUpdate('delivery-week', parent?.variant_id);
      }
      notice('Wunschlieferwoche aktualisiert.');
    } catch (error) {
      unresolved = Boolean(readJournal());
      try { latestCart = await request('cart.js'); await refresh(); } catch (_) { unresolved = true; }
      notice(error.message);
    } finally { busy = false; lock(); }
  }
  class DeliveryWeek extends HTMLElement {
    connectedCallback() {
      if (this.input) return;
      this.input = this.querySelector('select');
      this.display = this.querySelector('[data-delivery-display]');
      this.error = this.querySelector('[data-delivery-error]');
      this.update(this.dataset.mode === 'product' ? (productSelections.get(this.dataset.product) || '') : (this.dataset.current || ''));
      this.input.addEventListener('change', () => {
        this.updateDisplay();
        if (this.dataset.mode === 'cart') mutate(this);
        else {
          this.dataset.current = this.input.value;
          productSelections.set(this.dataset.product, this.input.value);
          controls().filter(el => el !== this && el.dataset.mode === 'product' && el.dataset.product === this.dataset.product).forEach(el => el.update(this.input.value));
        }
      });
      lock();
    }
    get available() { return weeks(this.dataset.from, this.dataset.to); }
    update(current = this.input.value) {
      const choices = this.available;
      this.input.replaceChildren(new Option('Wunschlieferwoche auswählen · optional', ''));
      for (const week of choices) this.input.add(new Option(week.label, week.code));
      this.invalid = Boolean(current && !choices.some(w => w.code === current));
      if (this.invalid) {
        const old = new Option((this.dataset.savedLabel || label(current)) + ' – nicht mehr verfügbar', current);
        old.disabled = true; this.input.add(old);
      }
      this.input.value = current;
      this.hidden = !choices.length && !current;
      this.error.textContent = this.invalid ? 'Bitte eine neue Wunschlieferwoche wählen oder die Auswahl entfernen.' : '';
      this.input.setAttribute('aria-invalid', String(this.invalid));
      this.updateDisplay();
      lock();
    }
    updateDisplay() {
      this.dataset.weekSelected = String(this.available.some(week => week.code === this.input.value));
      if (this.input.value) {
        this.display.textContent = this.dataset.mode === 'cart' && /^\d{4}-W\d{2}$/.test(this.input.value)
          ? `KW ${Number(this.input.value.slice(6))}`
          : this.input.selectedOptions[0]?.textContent || '';
      }
      else {
        this.display.replaceChildren(doc.createTextNode('Wunschlieferwoche auswählen '));
        const optional = doc.createElement('span'); optional.className = 'cstm-delivery-optional'; optional.textContent = '· optional'; this.display.append(optional);
      }
    }
  }
  customElements.define(selector, DeliveryWeek);
  api.pdpProperties = function (scope) {
    const el = scope?.querySelector(`${selector}[data-mode="product"]`);
    if (!el) return {};
    const current = el.input.value;
    el.update(current);
    if (el.invalid) { el.input.focus(); throw new Error('Bitte eine verfügbare Wunschlieferwoche wählen oder die Auswahl entfernen.'); }
    if (!el.available.length) return {};
    return properties({}, current, uuid());
  };
  api.quantityUpdate = async function (key, quantity, context = 'CartPage') {
    if (busy) return;
    busy = true; lock();
    try {
      const mini = doc.querySelector('mini-cart');
      await mini?.flushPendingQuantityUpdates();
      await mini?.mutationQueue;
      let cart = await request('cart.js');
      const parent = cart.items.find(i => i.key === key);
      if (!parent) throw new Error('Der Artikel wurde inzwischen geändert. Bitte erneut versuchen.');
      const updates = { [key]: Number(quantity) };
      if (parent.properties?.[GROUP] && !parent.properties?._karibu_parent) {
        for (const child of cart.items.filter(i => belongs(i, parent))) {
          if (Number(quantity) === 0 || child.original_price === 0) updates[child.key] = Number(quantity);
        }
      }
      const identities = new Map(cart.items.map(item => [item.key, identity(item) + '|' + (item.properties?.[GROUP] || '')]));
      cart = await request('cart/update.js', { updates });
      for (const [itemKey, expected] of Object.entries(updates)) {
        const updated = cart.items.find(i => i.key === itemKey) || cart.items.find(i => identity(i) + '|' + (i.properties?.[GROUP] || '') === identities.get(itemKey));
        if ((updated?.quantity || 0) !== expected) throw new Error('Die Menge konnte nicht übernommen werden. Bitte erneut versuchen.');
      }
      latestCart = cart;
      await refresh();
      doc.dispatchEvent(new CustomEvent(Number(quantity) === 0 ? 'cart:item-remove' : 'cart:item-quantity-update', {
        detail: { cart: latestCart, context }
      }));
      publishCartUpdate('cart-items', parent.variant_id);
    } catch (error) { notice(error.message); latestCart = await request('cart.js').catch(() => latestCart); await refresh().catch(() => {}); }
    finally { busy = false; lock(); }
  };
  api.validateCheckout = async function () {
    if (busy) return false;
    if (!cartControls().length && !readJournal()) return true;
    busy = true; lock();
    try {
      const cart = await request('cart.js'); latestCart = cart;
      const journal = readJournal();
      if (journal && (journal.token !== cart.token || !cart.items.some(i => journal.targets.some(t => i.key === t.key) || i.properties?.[GROUP] === journal.group))) saveJournal(null);
      await refresh(); // Current metafields and keys, not a stale page snapshot.
      cartControls().forEach(el => el.update(el.dataset.current || ''));
      unresolved = !checkGroups(cart);
      lock();
      if (readJournal() || unresolved || cartControls().some(el => el.invalid)) {
        notice('Bitte die Wunschlieferwoche korrigieren, entfernen oder die ausstehende Änderung erneut versuchen.');
        return false;
      }
      return true;
    } catch (_) { unresolved = true; notice('Der Lieferwunsch konnte nicht geprüft werden. Bitte erneut versuchen.'); return false; }
    finally { busy = false; lock(); }
  };
  doc.addEventListener('change', event => {
    if (busy && event.target.closest('mini-cart, cart-items')) { event.stopImmediatePropagation(); event.preventDefault(); }
  }, true);
  doc.addEventListener('submit', async event => {
    if (event.target.closest('mini-cart')) return; // MiniCart checks after its quantity queue.
    if (event.submitter?.name !== 'checkout') return;
    if (!cartControls().length && !readJournal()) return;
    event.preventDefault(); event.stopImmediatePropagation();
    const form = event.target;
    if (await api.validateCheckout()) {
      const input = doc.createElement('input'); input.type = 'hidden'; input.name = 'checkout'; input.value = 'checkout'; form.append(input);
      HTMLFormElement.prototype.submit.call(form);
    }
  }, true);
  doc.addEventListener('click', event => {
    if (busy && event.target.closest('mini-cart, cart-items, [name="checkout"], [href*="/checkout"]')) { event.preventDefault(); event.stopImmediatePropagation(); }
    const link = event.target.closest('a[href]');
    if (link && /\/checkout(?:[/?#]|$)/.test(link.getAttribute('href')) && (cartControls().length || readJournal())) {
      event.preventDefault(); event.stopImmediatePropagation();
      api.validateCheckout().then(valid => { if (valid) root.location.assign(link.href); });
    }
  }, true);
  root.addEventListener('pageshow', () => { controls().forEach(el => el.update(el.input.value)); lock(); if (readJournal()) notice('Eine Änderung der Wunschlieferwoche ist noch offen.'); });
  doc.addEventListener('visibilitychange', () => { if (!doc.hidden) { controls().forEach(el => el.update(el.input.value)); lock(); } });
  lock();
})(typeof window === 'undefined' ? globalThis : window);
